import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@workspace/ui/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive font-semibold cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-button-primary text-text-main shadow-xs hover:bg-button-primary-hover disabled:bg-[#D5D7DA]',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-button-secondary text-text-light shadow-xs hover:bg-black font-medium hover:text-white',
        ghost:
          'hover:bg-bg-gray hover:custom-border hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        gray: 'bg-[#EDEDEF] text-black border hover:bg-gray-300 px-3',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3 md:text-[1rem] text-sm',
        sm: 'h-8 rounded-md gap-1.5 text-sm',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4 md:text-[1rem] text-sm',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
